import { zodResolver } from '@hookform/resolvers/zod';
import { LANGUAGE_NAMES, SUPPORTED_LANGUAGES } from '@kiku/config/constants';
import type { InterviewModality } from '@kiku/interviews';
import { createJobInputSchema } from '@kiku/jobs/types';
import type { CreateJobInput } from '@kiku/jobs/types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InterviewFormatFields } from './interview-format-fields';

import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select';
import { Textarea } from '~/components/ui/textarea';

type BasicInfoStepProps = {
  defaultValues?: Partial<CreateJobInput>;
  onSubmit: (data: CreateJobInput) => void;
  isSubmitting: boolean;
};

export function BasicInfoStep({
  defaultValues,
  onSubmit,
  isSubmitting
}: BasicInfoStepProps): React.ReactNode {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<CreateJobInput>({
    resolver: zodResolver(createJobInputSchema),
    defaultValues: {
      modality: ['text', 'voice'],
      languages: ['en'],
      communicationLanguage: 'en',
      ...defaultValues
    }
  });

  const selectedLanguages = watch('languages');
  const selectedModalities = watch('modality');
  const communicationLanguage = watch('communicationLanguage');

  function toggleModality(m: InterviewModality): void {
    const current = selectedModalities;
    if (current.includes(m)) {
      setValue(
        'modality',
        current.filter((v) => v !== m),
        { shouldValidate: true }
      );
    } else {
      setValue('modality', [...current, m], { shouldValidate: true });
    }
  }

  function toggleLanguage(code: string): void {
    const current = selectedLanguages;
    if (current.includes(code)) {
      setValue(
        'languages',
        current.filter((l: string) => l !== code),
        { shouldValidate: true }
      );
    } else {
      setValue('languages', [...current, code], { shouldValidate: true });
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="flex flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="title">{t('wizard.basicInfo.title')}</Label>
        <Input
          id="title"
          placeholder={t('wizard.basicInfo.titlePlaceholder')}
          {...register('title')}
        />
        {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="description">{t('wizard.basicInfo.description')}</Label>
        <Textarea
          id="description"
          placeholder={t('wizard.basicInfo.descriptionPlaceholder')}
          className="min-h-[120px] max-h-[320px] overflow-y-auto"
          {...register('description')}
        />
        <p className="text-xs text-muted-foreground">{t('wizard.basicInfo.descriptionHint')}</p>
        {errors.description && (
          <p className="text-xs text-destructive">{errors.description.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="employer">{t('wizard.basicInfo.employer')}</Label>
        <Input
          id="employer"
          placeholder={t('wizard.basicInfo.employerPlaceholder')}
          {...register('employer')}
        />
        {errors.employer && <p className="text-xs text-destructive">{errors.employer.message}</p>}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="communicationLanguage">{t('wizard.basicInfo.communicationLanguage')}</Label>
        <Select
          value={communicationLanguage}
          onValueChange={(v) => {
            if (v) setValue('communicationLanguage', v, { shouldValidate: true });
          }}
        >
          <SelectTrigger id="communicationLanguage">
            <SelectValue>
              {LANGUAGE_NAMES[communicationLanguage as keyof typeof LANGUAGE_NAMES]}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {SUPPORTED_LANGUAGES.map((code) => (
              <SelectItem key={code} value={code}>
                {LANGUAGE_NAMES[code]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {t('wizard.basicInfo.communicationLanguageHint')}
        </p>
      </div>

      <InterviewFormatFields
        selectedModalities={selectedModalities}
        onModalityToggle={toggleModality}
        modalityError={!!errors.modality}
        selectedLanguages={selectedLanguages}
        onLanguageToggle={toggleLanguage}
        languagesError={!!errors.languages}
      />

      <div className="flex justify-end">
        <Button type="submit" disabled={isSubmitting}>
          {t('wizard.next')}
        </Button>
      </div>
    </form>
  );
}
